import { AuthState, AuthUser } from "../actions/authActions";
import { LOGIN, LOGIN_SUCCESS, LOGIN_FAILURE, LOGOUT, CLEAR_AUTH_ERROR, SET_MODULE_GRANTS } from "../types";
import { ACCOUNT_USER_KEY, clearCachedAccount } from "../../utils/account";
import { readStoredModuleGrants, storeModuleGrants } from "../../utils/moduleGrants";

// Load access from localStorage if available
const getStoredAccess = () => {
  try {
    const stored = localStorage.getItem('remnutri_access');
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

// The login response's name and email were held in redux only, so a reload
// rebuilt the user as { token, access } and every screen reading user_name — the
// TopBar avatar among them — saw undefined for the rest of the session.
const getStoredIdentity = () => {
  try {
    const stored = localStorage.getItem(ACCOUNT_USER_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

/**
 * The restored session, or null when it is not complete.
 *
 * Both halves are required. A token says who you are; `access` says what you
 * may open, and every screen is gated on it — a user restored without it is
 * signed in to nothing.
 */
const getStoredUser = () => {
  const token = localStorage.getItem('remnutri_auth_token');
  const access = getStoredAccess();
  if (token && access) {
    return {
      ...(getStoredIdentity() ?? {}),
      token,
      access,
      moduleGrants: readStoredModuleGrants(),
    };
  }
  return null;
};

/*
 * THE TWO MUST AGREE.
 *
 * `isAuthenticated` was `!!localStorage.getItem('remnutri_auth_token')` while
 * `user` required the token AND `access`. A browser holding one without the
 * other — an older build that stored access under a different key, a cleared
 * or unparseable value, a half-finished logout — came back authenticated with
 * no user.
 *
 * That is not a cosmetic mismatch. PublicOnlyRoute redirects away from /login
 * whenever isAuthenticated is true, so the login form could not be reached at
 * all: the app bounced to a landing path computed from no access, and the only
 * way back in was to clear the site's storage by hand.
 *
 * Deriving both from the same check makes a half-populated storage read as
 * signed out, which is what it is.
 */
const storedUser = getStoredUser();

const initialState: AuthState = {
  isAuthenticated: storedUser !== null,
  user: storedUser,
  loading: false,
  error: null,
};

const authReducer = (state: AuthState = initialState, action: any): AuthState => {
  switch (action.type) {
    case LOGIN:
      return {
        ...state,
        loading: true,
        error: null,
      };
    
    case LOGIN_SUCCESS:
      const user: AuthUser = action.payload;
      // Store token in localStorage
      localStorage.setItem('remnutri_auth_token', user.token);
      // Store access object as JSON in localStorage
      if (user.access) {
        localStorage.setItem('remnutri_access', JSON.stringify(user.access));
      }
      storeModuleGrants(user.moduleGrants ?? null);
      // A new session must not inherit the previous account's cached initials.
      clearCachedAccount();
      localStorage.setItem(
        ACCOUNT_USER_KEY,
        JSON.stringify({
          user_name: user.user_name,
          user_email: user.user_email,
          user_phone_number: user.user_phone_number,
        })
      );
      
      return {
        ...state,
        isAuthenticated: true,
        user,
        loading: false,
        error: null,
      };
    
    case LOGIN_FAILURE:
      // Remove token and access from localStorage on failure
      localStorage.removeItem('remnutri_auth_token');
      localStorage.removeItem('remnutri_access');
      storeModuleGrants(null);
      clearCachedAccount();
      
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: action.payload,
      };
    
    case LOGOUT:
      // Clear token and access from localStorage
      localStorage.removeItem('remnutri_auth_token');
      localStorage.removeItem('remnutri_access');
      localStorage.removeItem('setup');
      storeModuleGrants(null);
      clearCachedAccount();
      
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        loading: false,
        error: null,
      };
    
    // Re-read after load, so a change made in Settings → Authorization applies
    // on the next page load rather than only at the next sign-in.
    case SET_MODULE_GRANTS:
      if (!state.user) return state;
      storeModuleGrants(action.payload);
      return {
        ...state,
        user: { ...state.user, moduleGrants: action.payload },
      };

    case CLEAR_AUTH_ERROR:
      return {
        ...state,
        error: null,
      };
    
    default:
      return state;
  }
};

export default authReducer;
